import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import {
    floorGameColor as color,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledLayout,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './tokens';

interface CommonActionProps {
    $compact?: boolean;
    $disabled?: boolean;
}

interface ConicTimerProps {
    $degree?: number;
}

const actionBase = css<CommonActionProps>`
    ${styledReset.borderBox};

    ${styledInteraction.focusRing({
        offset: -tokens.focusRing.offset,
        radius: tokens.radius,
    })};

    --yat_timed-action-padding-x: ${({ $compact }) => ($compact ? tokens.padding.compact : tokens.padding.default)}px;
    --yat_timed-action-height: ${({ $compact }) => ($compact ? tokens.height.compact : tokens.height.default)}px;
    --yat_timed-action-font-size: ${({ $compact }) => ($compact ? tokens.fontSize.compact : tokens.fontSize.default)}px;

    --yat_timed-action-glow-opacity: 0;
    --yat_timed-action-content-bg-color: ${color.onyx};

    z-index: ${tokens.zindex.wrapper};
    position: relative;
    display: grid;
    align-items: stretch;
    height: var(--yat_timed-action-height);
    transition: opacity ${duration.normal}ms ${easing.cubic};

    ${({ $disabled }) =>
        $disabled &&
        css`
            ${styledInteraction.interactionDisable};
            opacity: 0.8;
        `};

    &:hover {
        --yat_timed-action-glow-opacity: 1;
        --yat_timed-action-content-bg-color: ${color.jet};
    }

    &:active {
        --yat_timed-action-glow-opacity: 0.6;
        --yat_timed-action-content-bg-color: ${color.onyx};
    }

    &::after {
        ${styledInteraction.interactionDisable};
        ${styledLayout.positionCover({ offset: 1 })};
        content: '';
        display: block;
        border-radius: ${tokens.radius}px;
        box-shadow: 0 0 ${tokens.glow}px rgba(255, 255, 255, 0.4);
        opacity: var(--yat_timed-action-glow-opacity);
        transition: opacity ${duration.normal}ms ${easing.cubic};
    }
`;

export const Action = styled.button.attrs({ type: 'button' })<CommonActionProps>`
    ${styledReset.button};
    ${actionBase};
`;

export const ExternalLink = styled.a.attrs({
    target: '_blank',
    rel: 'noopener noreferrer',
})<CommonActionProps>`
    ${styledReset.link};
    ${actionBase};
`;

export const InternalLink = styled(Link)<CommonActionProps>`
    ${styledReset.link};
    ${actionBase};
`;

export const Interior = styled.div`
    z-index: ${tokens.zindex.interior};
    position: relative;
    display: grid;
    align-items: stretch;
    padding: ${tokens.wrapper.borderWidth}px;
    background-color: ${color.ash};
    border-radius: ${tokens.radius}px;
    overflow: hidden;
`;

export const ConicTimer = styled.div<ConicTimerProps>`
    ${styledInteraction.interactionDisable};
    ${styledLayout.positionCover()};

    --yat_timed-action-conic-deg: ${({ $degree }) => ($degree ? `${$degree}deg` : '0deg')};

    z-index: ${tokens.zindex.conicTimer};
    background: conic-gradient(
        transparent var(--yat_timed-action-conic-deg),
        ${color.white} var(--yat_timed-action-conic-deg) 360deg
    );
`;

export const Content = styled.div`
    ${styledLayout.fullWidthFix};
    z-index: ${tokens.zindex.content};
    position: relative;
    display: grid;
    place-content: center;
    place-items: center;
    padding-left: var(--yat_timed-action-padding-x);
    padding-right: var(--yat_timed-action-padding-x);
    text-align: center;
    border-radius: ${tokens.radius}px;
    background-color: var(--yat_timed-action-content-bg-color);
    transition: background-color ${duration.normal}ms ${easing.cubic};
`;

export const Label = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.truncate};
    font-size: var(--yat_timed-action-font-size);
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${color.white};
`;
